/**
 * Putting a number on the do-not-call list by hand.
 *
 * Two reasons this exists next to the row menu that marks a call: a request can
 * arrive somewhere other than a call - by mail, through the contact form, from
 * a colleague - and a number can need blocking before anybody has ever rung it.
 *
 * Everything typed here is `manual`, which is the only kind of entry that can be
 * taken off again. The form deliberately cannot create the permanent kind: an
 * operator typing a number in should not be able to make it unremovable by
 * picking the wrong item in a dropdown, and the entry that IS permanent is
 * written where the person actually said it - on the call.
 */
import { useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { addPhoneSuppression, type PhoneSuppression } from "../../../../lib/callSuppression";
import { normalizeE164 } from "../../../../lib/phone";
import { Btn, Modal } from "../../../ui";

export function AddNumberModal({
  onClose,
  onAdded,
  onError,
}: {
  onClose: () => void;
  onAdded: (row: PhoneSuppression) => void;
  onError: () => void;
}) {
  const t = useT();
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  // Checked here as well as on the server, and not to save the request: the
  // operator finds out while the number is still in front of them, rather than
  // after a dialog has closed over it.
  const normalized = normalizeE164(phone);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!normalized) return;
    setBusy(true);
    try {
      const res = await addPhoneSuppression({
        phone: normalized,
        source: "manual",
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      onAdded(res.suppression);
    } catch {
      onError();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t("calls.suppression.add")}
      label={t("calls.suppression.add")}
      onClose={onClose}
      className="max-w-[460px]"
    >
      <form onSubmit={submit} className="mt-1 flex flex-col gap-3">
        <div>
          <span className="crm-lab">{t("calls.suppression.col.number")} *</span>
          <label className="crm-inp">
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder={t("calls.suppression.numberPlaceholder")}
              inputMode="tel"
              autoComplete="off"
              spellCheck={false}
              autoFocus
              required
            />
          </label>
          {/* Only once there is something to judge. A red line under an empty
              field is the form telling somebody off for not having typed yet. */}
          {phone.trim() && !normalized ? (
            <span className="mt-1 block text-[11.5px] text-destructive">{t("calls.suppression.badNumber")}</span>
          ) : null}
        </div>

        <div>
          <span className="crm-lab">{t("calls.suppression.col.note")}</span>
          <label className="crm-inp">
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("calls.suppression.notePlaceholder")}
              autoComplete="off"
            />
          </label>
        </div>

        <p className="crm-sub">{t("calls.suppression.addManualHint")}</p>

        <div className="crm-m-foot">
          <Btn type="button" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Btn>
          <Btn type="submit" tone="brand" disabled={busy || !normalized}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
            {t("calls.suppression.add")}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}
